/**
 * The organiser's side of a `COUNTER` (agora#284): finding the calendar
 * event a received `COUNTER` proposes a new time for, and telling whether
 * this account actually organises it. Kept apart from `CounterCard.tsx` so
 * the calendar lookup and the date arithmetic can be tested without
 * rendering anything.
 */
import type { CalendarClient, CalendarEvent } from '@wtfalch/calendar/calendar';
import type { Imip } from '@wtfalch/email/imip';
import { imipOf } from '@wtfalch/email/imip';
import type { MailClient, Message } from '@wtfalch/email/mailbox';

import { durationMinutes, toIsoDuration } from '../calendar/layout.ts';
import { isOrganiser, myParticipant } from '../calendar/participants.ts';

export type CounterImip = Imip & { method: 'COUNTER' };

/** Case-insensitive address equality -- `calendar/participants.ts`'s own
 *  `sameAddress`, kept local here since that one is private to that file. */
function sameEmail(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * A local, offset-free date-time string in `timeZone` (or UTC for a
 * floating or zoneless event) -- the shape `CalendarEventDraft.start` and
 * `CalendarEvent.recurrenceId` both use (RFC 8984's `LocalDateTime`).
 * `en-CA` gives year-month-day ordering for free; `h23` keeps midnight as
 * `00`, never `24`.
 */
export function toLocalDateTime(date: Date, timeZone: string | null): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZone ?? 'UTC',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
}

/** Whole minutes between two instants, floored at zero -- `toIsoDuration`'s
 *  own input. */
export function minutesBetween(start: Date, end: Date): number {
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60_000));
}

/** Minutes in an ISO 8601 duration, `layout.ts`'s own `durationMinutes`
 *  (`PT1H`/`PT30M`) widened to also read a whole-day `P<n>D` -- what an
 *  all-day `CalendarEvent.duration` can carry and `layout.ts`'s own parser,
 *  built only for `CalendarEventDraft.duration`'s timed shape, does not. */
export function durationInMinutes(iso: string): number {
  const days = /^P(\d+)D$/.exec(iso);
  if (days) return Number(days[1]) * 24 * 60;
  return durationMinutes(iso);
}

/**
 * The `duration` to write back for a proposed `start`/`end` span -- a
 * whole-day `P<n>D` for an all-day event, matching the shape an all-day
 * `duration` already carries, or an hour/minute `toIsoDuration` (`layout.ts`)
 * for a timed one. Without this, accepting a proposal on an all-day event
 * rewrote a `P1D` duration to `PT24H`: still 24 hours, but no longer the
 * whole-day shape every other all-day event on the account carries.
 */
export function eventDuration(minutes: number, isAllDay: boolean): string {
  if (isAllDay) return `P${Math.max(1, Math.round(minutes / (24 * 60)))}D`;
  return toIsoDuration(minutes);
}

/**
 * The organiser's own copy of the event a `COUNTER` proposes a new time
 * for, or `null` when this account's calendars have none.
 *
 * `listEvents`, not `searchEvents`: a `COUNTER`'s `summary` is mail content
 * the proposer wrote, not something to trust as a search term for my own
 * calendar, and `listEvents` already lists only calendars this account
 * holds (never one merely shared with it -- `@wtfalch/calendar`'s own
 * `fakeCalendar` doc comment). A two-year window centred on today is wide
 * enough for any event still worth countering.
 *
 * `recurrenceId`, when the counter carries one, picks the one occurrence
 * of a recurring series it was proposed against -- matched by converting
 * it into that occurrence's own zone, the same transform `toLocalDateTime`
 * uses to write a new start back (`CounterCard.tsx`'s `accept`).
 */
function twoYearWindow(): { after: string; before: string } {
  const now = new Date();
  return {
    after: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString(),
    before: new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()).toISOString(),
  };
}

export async function findOrganizedEvent(
  calendarClient: CalendarClient,
  imip: CounterImip,
): Promise<CalendarEvent | null> {
  const events = await calendarClient.listEvents(twoYearWindow());
  const candidates = events.filter((e) => e.uid === imip.uid);
  const { recurrenceId } = imip;
  if (recurrenceId) {
    return (
      candidates.find((e) => e.recurrenceId === toLocalDateTime(recurrenceId, e.timeZone)) ?? null
    );
  }
  return candidates.find((e) => !e.recurrenceId) ?? candidates[0] ?? null;
}

/**
 * The `COUNTER` a received message carries, and the organiser's own event
 * it proposes a new time for -- `null` for everything `ThreadPane` keeps
 * today's plain-chip behaviour for: no calendar part, a method other than
 * `COUNTER`, an event this account's calendars do not have, one this
 * account is not the organiser of (an invitee's own copy of someone else's
 * event, say), or -- the two checks below -- a `COUNTER` naming a proposer
 * who is not who they claim, or not actually on the event.
 *
 * **Two checks, not one, because `imip.attendee` is mail content the
 * proposer wrote, not a fact.** A message can claim any `attendee` it
 * likes in its calendar part, so before that name reaches a live
 * Accept/Decline this also requires `message.from` to be the same address,
 * and requires that address to already be a participant on the organiser's
 * own event. Either alone is not enough: a matching `from` on a stranger's
 * own address still is not someone invited, and a truthful `attendee`
 * field on a message actually sent by someone else is a forgery either way.
 *
 * **`message.from` is the message's `From` header, not an independently
 * authenticated envelope sender.** `@wtfalch/email`'s `toMessage`
 * (`packages/email/src/mailbox/thread.ts`) copies it straight from the
 * JMAP `Email` object's `from` property -- the author line the sender
 * wrote, the same as `imip.attendee`, not anything the receiving mail
 * server verified. So this pair of checks constrains a forged `attendee`
 * to also match the `From` header and name an existing participant, but on
 * its own says nothing about who authenticated the message: a spoofed
 * `From` that happens to match an existing attendee's address, or a
 * genuine attendee's proposal simply forwarded by someone else, both pass
 * today. Those checks stay; `CounterCard` adds the verdict (agora#348):
 * it warns before Accept unless `message.senderAuthenticated` is true, the
 * mail server's own DKIM/DMARC result, which `@wtfalch/email` reads only
 * when the agora config names the server's `mailAuthServId`.
 */
export async function loadOrganizedCounter(
  client: MailClient,
  calendarClient: CalendarClient,
  message: Message,
): Promise<{ imip: CounterImip; event: CalendarEvent } | null> {
  const imip = await imipOf(client, message);
  if (imip?.method !== 'COUNTER') return null;
  // Exactly one author: `senderAuthenticated` is read for `from[0]` alone, so
  // a second address in `From` must not be able to borrow that verdict.
  const [sender] = message.from;
  if (message.from.length !== 1 || !sender || !sameEmail(sender.email, imip.attendee.email))
    return null;
  const event = await findOrganizedEvent(calendarClient, imip);
  if (!event) return null;
  if (!event.participants?.some((p) => sameEmail(p.calendarAddress, imip.attendee.email))) {
    return null;
  }
  const identity = await calendarClient.identity();
  const mine = myParticipant(event, identity);
  if (!mine || !isOrganiser(mine)) return null;
  return { imip, event };
}

/**
 * RFC 5545 §3.1 line folding, undone: a continuation line begins with a
 * single space or tab, folded there so no line in the file runs past 75
 * octets, and unfolding is just removing that break and the whitespace
 * straight after it. A long value -- a long Outlook `UID` above all --
 * routinely folds across two or more lines, and left folded, `icsUid`'s own
 * regex below would read only the first line's worth and truncate the rest
 * (agora#222 refuter finding: a real event then shows as "Not on your
 * calendars" for no reason but its own UID being long).
 */
function unfold(ics: string): string {
  return ics.replace(/\r?\n[ \t]/g, '');
}

/**
 * A `VEVENT`'s own `UID`, read directly out of its `.ics` (or `text/calendar`)
 * bytes -- never through `parseImip`/`imipOf`, which only ever answer a
 * `COUNTER` or a `DECLINECOUNTER` and leave every ordinary invitation
 * (`REQUEST`, `REPLY`, `CANCEL`) `null` (this module's own `Imip`, and
 * `@wtfalch/email/imip/parse.ts`'s own doc comment: "any other method... all
 * of them the server's own iTIP engine owns"). `UID` is the one property RFC
 * 5545 requires on every method alike, so a plain regex read is enough here
 * -- "Open in Calendar" (agora#222) only ever needs that one line back out of
 * text this account's own mail server already accepted as calendar data, not
 * `ical.js`'s full grammar. `null` for anything that does not carry one.
 *
 * `unfold` runs first: `UID` is exactly the kind of long, structured value
 * (a GUID plus vendor prefix, on a real Outlook invite) that gets folded
 * across lines, and matching the raw, still-folded text would only ever see
 * its first line.
 */
export function icsUid(ics: string): string | null {
  const match = /^UID[^:\r\n]*:(.+)$/im.exec(unfold(ics));
  return match?.[1]?.trim() || null;
}

/**
 * Any event on my own calendars carrying this UID, over the same two-year
 * window `findOrganizedEvent` searches -- widened here to every invitation
 * `ThreadPane`'s `isInvite` recognises, not only a `COUNTER` this account
 * organises: "Open in Calendar" (agora#222) only needs the event to exist
 * somewhere on my calendars, never that I own it. `null` when nothing on my
 * calendars carries this UID at all.
 */
export async function findEventByUid(
  calendarClient: CalendarClient,
  uid: string,
): Promise<CalendarEvent | null> {
  const events = await calendarClient.listEvents(twoYearWindow());
  const candidates = events.filter((e) => e.uid === uid);
  return candidates.find((e) => !e.recurrenceId) ?? candidates[0] ?? null;
}

/**
 * The `YYYY-MM-DD` `CalendarLocation.date` wants, straight off an event's own
 * `start` -- already a local, offset-free date-time (`CalendarEvent.start`'s
 * own doc comment), so no zone conversion is needed the way `toLocalDateTime`
 * above does for an instant that still has one.
 */
export function eventDateParam(event: CalendarEvent): string {
  return event.start.slice(0, 10);
}
